import { Axios, CancelToken } from "axios";
import useNotificationHandler from "hooks/useNotificationHandler";
import { QUERY_KEYS } from "library/shared";
import { IAzureSearchResult, IUserSettings } from "library/types";
import { ExpandedColumn } from "library/types/columns";
import { DetailsListSortProp } from "library/types/types_old";
import { createAzureFilterParam } from "library/utils/azureSearchFilterParam";
import { UseQueryResult, useQuery, useQueryClient } from "react-query";
import api from "service/api";

type Params = {
  skip;
  top: number;
  search: string;
  sort: any;
  columns: ExpandedColumn[];
  enabled?: boolean;
};

export const getOffers = async ({
  skip,
  top,
  search,
  sort,
  columns,
}: Params) => {
  const response = await searchOffers({
    skip,
    top,
    search,
    sort: { key: "", dir: "" },
    columns,
  });

  return response.data as IAzureSearchResult;
};

export function useGetOffers({
  skip,
  top,
  search,
  sort,
  columns,
  enabled = true,
}: Params): UseQueryResult<IAzureSearchResult> {
  const { handleAddAxiosErrorNotification } = useNotificationHandler();

  return useQuery<IAzureSearchResult, Error>(
    [QUERY_KEYS.offers, skip, top, search, sort],
    () => getOffers({ skip, top, search, sort, columns }),
    {
      enabled: enabled,
      onError: (error: any) => {
        handleAddAxiosErrorNotification(error);
      },
    }
  );
}

export const useInvalidateUserAppSettings = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.offers,
    });
};

export type SearchOffersArgs = {
  skip: number;
  top: number;
  search: string;
  sort: DetailsListSortProp;
  cancelToken?: CancelToken;
  columns: ExpandedColumn[];
};

export const searchOffers = async ({
  skip,
  top,
  search,
  sort,
  cancelToken,
  columns,
}: SearchOffersArgs) => {
  let searchUrl = `/offers`;
  searchUrl += `?searchCriteria=${search.replace(
    /&/g,
    encodeURIComponent("&")
  )}*`;
  if (sort.key !== "" && sort.dir !== "") {
    searchUrl += `&$orderby=${sort.key} ${sort.dir}`;
  }
  searchUrl += `&form=json`;
  searchUrl += `&$count=true`;
  searchUrl += `&$top=${top}&$skip=${skip}`;

  let filterStr = createAzureFilterParam(columns as any);

  if (filterStr.length > 0) {
    searchUrl += `&$filter=${filterStr}`;
  }

  const res = await api.get(searchUrl, {
    cancelToken,
  });
  return res;
};
